export function canUseLocalStorage(): boolean {
  try {
    return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
  } catch {
    return false;
  }
}

/**
 * Creates a namespaced set of localStorage helpers for persisting job IDs.
 * Keeps each job type's storage isolated via a unique prefix.
 */
export function makeJobStorage(prefix: string) {
  return {
    persist(key: string, jobId: string): void {
      if (!canUseLocalStorage()) return;
      window.localStorage.setItem(`${prefix}${key}`, jobId);
    },
    read(key: string): string | null {
      if (!canUseLocalStorage()) return null;
      return window.localStorage.getItem(`${prefix}${key}`);
    },
    clear(key: string): void {
      if (!canUseLocalStorage()) return;
      window.localStorage.removeItem(`${prefix}${key}`);
    },
  };
}

/**
 * Like makeJobStorage, but stores an array of job IDs per key so multiple
 * concurrent jobs for the same parent can all be tracked and resumed.
 */
export function makeMultiJobStorage(prefix: string) {
  function readRaw(key: string): string[] {
    if (!canUseLocalStorage()) return [];
    const raw = window.localStorage.getItem(`${prefix}${key}`);
    if (!raw) return [];
    try { return JSON.parse(raw) as string[]; } catch { return []; }
  }
  return {
    persist(key: string, jobId: string): void {
      if (!canUseLocalStorage()) return;
      const ids = readRaw(key);
      if (!ids.includes(jobId)) {
        ids.push(jobId);
        window.localStorage.setItem(`${prefix}${key}`, JSON.stringify(ids));
      }
    },
    read(key: string): string[] {
      return readRaw(key);
    },
    remove(key: string, jobId: string): void {
      if (!canUseLocalStorage()) return;
      const ids = readRaw(key).filter((id) => id !== jobId);
      if (ids.length === 0) {
        window.localStorage.removeItem(`${prefix}${key}`);
      } else {
        window.localStorage.setItem(`${prefix}${key}`, JSON.stringify(ids));
      }
    },
    clear(key: string): void {
      if (!canUseLocalStorage()) return;
      window.localStorage.removeItem(`${prefix}${key}`);
    },
  };
}

export async function sleepMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Generic adaptive polling loop shared by both single-item and bulk flows.
 *
 * Calls `fetcher` repeatedly, honouring the `poll_after_ms` hint returned by
 * the server, until the result has status "completed" or "failed".
 * Falls back to `makeTimeoutResult()` if `timeoutMs` is exceeded.
 */
export async function pollUntilTerminal<T extends { status: string; poll_after_ms?: number }>(
  fetcher: () => Promise<T>,
  makeTimeoutResult: () => T,
  opts: { timeoutMs: number; initialPollMs?: number },
): Promise<T> {
  const start = Date.now();
  let pollAfterMs = Math.max(250, Math.min(opts.initialPollMs ?? 1000, 5000));

  while (Date.now() - start < opts.timeoutMs) {
    await sleepMs(pollAfterMs);
    const result = await fetcher();
    if (result.status === "completed" || result.status === "failed") {
      return result;
    }
    pollAfterMs = Math.max(250, Math.min(result.poll_after_ms ?? pollAfterMs, 5000));
  }

  return makeTimeoutResult();
}
